// @flow
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import * as React from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { Box } from 'grommet';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { withRouter } from 'react-router';
import { withLeaflet } from 'react-leaflet';
import { isEmpty, sortBy } from 'lodash';
import MeatballsIcon from 'esm-web-pro/components/layout/sidePanel/icons/MeatballsIcon';
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import ConnectIcon from 'esm-web-pro/components/layout/sidePanel/icons/ConnectIcon';
import point11Messages from 'esm-web-pro/messages/points11Messages';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import { msg } from 'esm-web-pro/containers/pages/network/objects/point11/Point11CableList';
import { numberPointsCallbacksCable } from 'esm-web-pro/containers/pages/network/objects/cable/MapCable';
import UseHighlightCable from 'esm-web-pro/containers/pages/network/objects/cable/useHighlightCable';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';
import ContentLabel from 'esm-web-pro/components/layout/sidePanel/ContentLabel';

type Props = {
    cables: Object,
    point11: Object,
    intl: IntlShape,
    onConnectCable: Function,
    history: Object,
    match: Object,
    getCableData: Function,
    mapIsDraft: boolean,
    editor: BaseEditor,
    leaflet: LeafletContext,
    onRowClick: Function,
    clickedRow: ?string,
};

const DisconnectedCablesList = (props: Props) => {
    const {
        intl: { formatMessage }, cables, onConnectCable, history: { push }, match: { url },
        getCableData, mapIsDraft, editor, leaflet: { map }, onRowClick, clickedRow, point11,
    } = props;

    const handleHighlight = (cable) => {
        editor.unregisterAllLoadingCallback();
        const path = cable.path.map((c) => [c[1], c[0]]);
        const focusCableCallback = UseHighlightCable(path);
        editor.registerLoadingCallback(focusCableCallback);
        editor.registerMultipleLoadingCallbacks(numberPointsCallbacksCable(cable));
        const point11Coordinates = [point11.coordinates.coordinates[1], point11.coordinates.coordinates[0]];

        if (map) {
            map.highlightedGeometry = {
                bounds: path, center: point11Coordinates,
            };
            map.flyToBoundsCenter(path, point11Coordinates);
        }
        editor.reloadFeatures();
        onRowClick(`cable-${cable.id}`);
    };

    return (
        <Box key={`disconnected-cables-${point11.id}`}>
            <ContentLabel>
                {`${formatMessage(msg.disconnected)} 
                (${formatMessage(point11Messages[point11.type])} ${point11.sequence})`}
            </ContentLabel>
            <Box>
                {(isEmpty(cables)) && (
                    <ContentRow padLevel={1}>
                        <ValueText>{formatMessage(msg.noDisconnected)}</ValueText>
                    </ContentRow>
                )}
                {sortBy(cables, ['sequence']).map((cable) => {
                    const actions = [
                        {
                            key: `cable-${cable.id}-more`,
                            icon: <MeatballsIcon />,
                            size: '20px',
                            width: '3px',
                            callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                const path = url.split('/layer');
                                push(`${path[0]}/layer/cable/${cable.identity}`);
                            },
                            tooltip: formatMessage(actionMessages.more),
                        },
                    ];

                    if (mapIsDraft) {
                        actions.unshift({
                            key: `cable-${cable.id}-connect`,
                            icon: <ConnectIcon />,
                            size: '20px',
                            width: '3px',
                            callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                onConnectCable(true, cable);
                            },
                            tooltip: formatMessage(actionMessages.connect),
                            disabled: isEmpty(point11.connected_cables.layers1_2),
                        });
                    }

                    return (
                        <ContentRow
                            key={`disconnected-cable-${cable.id}`}
                            actions={<RowActions actions={actions} />}
                            onClick={() => handleHighlight(cable)}
                            rowId={`cable-${cable.id}`}
                            clickedRowIdentity={clickedRow}
                            padLevel={1}
                        >
                            <ValueText>
                                {getCableData(cable)}
                            </ValueText>
                        </ContentRow>
                    );
                })}
            </Box>
        </Box>
    );
};

export default withLeaflet(withRouter(injectIntl(DisconnectedCablesList)));
